import { useCallback, useEffect, useRef, useState } from "react";
import IntegrationApi from "../apis/integrationApi";
import {
  asNinjaOneOAuthCallbackMessage,
  buildNinjaOneAuthorizationUrl,
  createNinjaOneOAuthState,
  getNinjaOneRedirectUri,
  normalizeNinjaOneInstanceUrl,
  NinjaOneOAuthRequest,
} from "../utils/ninjaOneOAuth";

interface NinjaOneRefreshTokenOptions {
  instanceUrl: string;
  clientId: string;
  clientSecret: string;
  contextKey: string;
  enabled?: boolean;
  onStart: () => void;
  onSuccess: (refreshToken: string) => void;
  onError: (message: string) => void;
}

interface PendingSignIn {
  request: NinjaOneOAuthRequest;
  popup: Window;
  exchanging: boolean;
  closeTimer?: number;
}

// Shared sign-in and token exchange for onboarding, Add Integration, and configuration.
export function useNinjaOneRefreshToken(options: NinjaOneRefreshTokenOptions) {
  const [isGettingRefreshToken, setIsGettingRefreshToken] = useState(false);
  const pendingRef = useRef<PendingSignIn | null>(null);
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  const finishSignIn = useCallback((pending: PendingSignIn) => {
    window.clearInterval(pending.closeTimer);
    if (pendingRef.current !== pending) return;
    pendingRef.current = null;
    if (!pending.popup.closed) pending.popup.close();
    setIsGettingRefreshToken(false);
  }, []);

  useEffect(() => {
    setIsGettingRefreshToken(false);
    if (options.enabled === false) return;
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = asNinjaOneOAuthCallbackMessage(event.data);
      const pending = pendingRef.current;
      if (!message || !pending || pending.exchanging || event.source !== pending.popup || message.state !== pending.request.state) return;

      if (message.error || !message.code) {
        callbacksRef.current.onError(message.errorDescription || message.error || "NinjaOne did not return the expected sign-in result.");
        finishSignIn(pending);
        return;
      }

      pending.exchanging = true;
      try {
        const result = await new IntegrationApi().exchangeNinjaOneAuthorizationCode({
          instance_url: pending.request.instanceUrl,
          client_id: pending.request.clientId,
          client_secret: pending.request.clientSecret,
          code: message.code,
          redirect_uri: pending.request.redirectUri,
        });
        if (pendingRef.current === pending) callbacksRef.current.onSuccess(result.refresh_token);
      } catch (error: any) {
        if (pendingRef.current === pending) {
          callbacksRef.current.onError(
            error.response?.data?.details || error.response?.data?.error || error.message || "Failed to create Refresh Token."
          );
        }
      } finally {
        finishSignIn(pending);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) {
        window.clearInterval(pending.closeTimer);
        if (!pending.popup.closed) pending.popup.close();
      }
    };
  }, [options.contextKey, options.enabled, finishSignIn]);

  const startSignIn = () => {
    if (options.enabled === false || pendingRef.current) return;
    const instanceUrl = normalizeNinjaOneInstanceUrl(options.instanceUrl);
    const clientId = options.clientId.trim();
    const clientSecret = options.clientSecret.trim();
    if (!instanceUrl || !clientId || !clientSecret) {
      options.onError("Fill in Instance URL, Client ID, and Client Secret before getting the Refresh Token.");
      return;
    }

    const request: NinjaOneOAuthRequest = {
      instanceUrl,
      clientId,
      clientSecret,
      redirectUri: getNinjaOneRedirectUri(),
      state: createNinjaOneOAuthState(),
    };
    let authorizationUrl: string;
    try {
      authorizationUrl = buildNinjaOneAuthorizationUrl(request);
    } catch {
      options.onError("Enter a valid NinjaOne Instance URL before getting the Refresh Token.");
      return;
    }

    options.onStart();
    const popup = window.open(authorizationUrl, "theo-ninjaone-oauth", "popup=yes,width=720,height=820");
    if (!popup) {
      options.onError("Allow pop-ups for Theo, then try getting the Refresh Token again.");
      return;
    }

    const pending: PendingSignIn = { request, popup, exchanging: false };
    pendingRef.current = pending;
    setIsGettingRefreshToken(true);
    pending.closeTimer = window.setInterval(() => {
      if (popup.closed && !pending.exchanging && pendingRef.current === pending) {
        callbacksRef.current.onError("NinjaOne sign-in was closed. Try getting the Refresh Token again.");
        finishSignIn(pending);
      }
    }, 500);
  };

  return { isGettingRefreshToken, startSignIn };
}
